import React, { useEffect, useRef, useState } from 'react';
import * as UTIF from 'utif';
import ImageDownloadIcon from './ImageDownloadIcon';

/**
 * TiffRenderer Component
 * Renders TIFF images using the UTIF library
 * Converts TIFF to canvas for display in document viewer
 *
 * @param {string} docValue - The data URI of the TIFF image
 * @param {string} docName - The name of the TIFF document
 */
const TiffRenderer = ({ docValue, docName }) => {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const renderTIFF = async () => {
      try {
        setLoading(true);
        setError(null);

        // Extract base64 data from data URI
        const base64Data = docValue.split(',')[1];
        if (!base64Data) {
          throw new Error('Invalid TIFF data format');
        }

        // Convert base64 to array buffer
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const buffer = bytes.buffer;

        // Decode TIFF
        const ifds = UTIF.decode(buffer);
        if (!ifds.length) throw new Error('No image data found in TIFF');

        UTIF.decodeImage(buffer, ifds[0]);
        const rgba = UTIF.toRGBA8(ifds[0]);

        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;

        const ctx = canvas.getContext('2d');
        const imageData = new ImageData(
          new Uint8ClampedArray(rgba),
          ifds[0].width,
          ifds[0].height
        );
        ctx.putImageData(imageData, 0, 0);
        setLoading(false);
      } catch (err) {
        console.error('TIFF render error:', err);
        setError('Failed to render TIFF image');
        setLoading(false);
      }
    };

    if (docValue) {
      renderTIFF();
    }
  }, [docValue]);

  if (error) {
    return (
      <div className="document-preview tiff-preview min_h_200x d-flex justify_content_center">
        <div className="document-icon">⚠️</div>
        <p className="text-muted mb-2 text-center font_15 text-black">
          {error}
        </p>
        <a
          href={docValue}
          download={docName || 'image.tiff'}
          className="document-download-link"
        >
          Download TIFF Image
        </a>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {loading && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          Loading TIFF image...
        </div>
      )}
      <ImageDownloadIcon docValue={docValue} docName={docName} />
      <canvas ref={canvasRef} className="document-image" />
    </div>
  );
};

export default TiffRenderer;
